import { useState, useEffect } from "react";
import "../../../Styles/style.css";
import AdminApiModule from "../../../components/AdminApi/Api/api";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import Button from "@mui/material/Button";
import { useNavigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";

// Permission check utility function
const checkPermission = (userPermissions, permissionPath) => {
  if (!userPermissions) return false;

  // Convert the permission path to an array and check nested structure
  const pathParts = permissionPath.split(".");
  let currentLevel = userPermissions;

  for (const part of pathParts) {
    if (
      currentLevel &&
      typeof currentLevel === "object" &&
      part in currentLevel
    ) {
      currentLevel = currentLevel[part];
    } else {
      return false;
    }
  }

  return currentLevel === true;
};

const AdminCenterOnboarders = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const reduxState = useSelector((state) => state.userReducer);

  // Get user permissions from Redux state
  const userPermissions = reduxState?.adminDetails?.role_id?.permissions;

  const [centerOnboardersList, setCenterOnboardersList] = useState([]);
  const [isCenterOnboarderEdit, setIsCenterOnboarderEdit] = useState(false);
  const [viewfeedback, setViewFeedback] = useState([]);
  const [isViewCenterOnboarderOpen, setIsViewCenterOnboarderOpen] =
    useState(false);
  const [selectedCenterOnboarder, setSelectedCenterOnboarder] = useState({});
  const [toast, setToast] = useState({
    open: false,
    msg: "",
    duration: 5000,
    type: "",
  });

  // Permission check functions
  const hasFullAccess = () => {
    return checkPermission(
      userPermissions,
      "center_onboard_details.full_access"
    );
  };

  const hasAddPermission = () => {
    return (
      hasFullAccess() ||
      checkPermission(
        userPermissions,
        "center_onboard_details.update_center_onboard_details.add_center_onboard"
      )
    );
  };

  const hasViewPermission = () => {
    return (
      hasFullAccess() ||
      checkPermission(
        userPermissions,
        "center_onboard_details.update_center_onboard_details.view_center_onboard"
      )
    );
  };

  const hasUpdatePermission = () => {
    return (
      hasFullAccess() ||
      checkPermission(
        userPermissions,
        "center_onboard_details.update_center_onboard_details.update_center_onboard"
      )
    );
  };

  const hasAnyAccess = () => {
    return (
      hasFullAccess() ||
      checkPermission(
        userPermissions,
        "center_onboard_details.update_center_onboard_details.view_center_onboard"
      ) ||
      checkPermission(
        userPermissions,
        "center_onboard_details.update_center_onboard_details.add_center_onboard"
      ) ||
      checkPermission(
        userPermissions,
        "center_onboard_details.update_center_onboard_details.update_center_onboard"
      )
    );
  };

  useEffect(() => {
    getAllCenterOnBoarders();
  }, []);

  const showDialog = (open, type, msg) => {
    setToast({
      open: open,
      msg: msg,
      duration: 5000,
      type: type,
    });
  };

  const closeDialog = (open = false) => {
    setToast({
      open: open,
      msg: "",
      duration: 5000,
      type: "",
    });
  };

  const getFormattedList = (array) => {
    let result = [];
    array &&
      array.length > 0 &&
      array.map((item) => {
        result.push(item);
      });
    return result.length > 0 ? result.join(", ") : "";
  };

  const getAllCenterOnBoarders = () => {
    // Check view permission before making API call
    if (!hasViewPermission()) {
      showDialog(
        true,
        "error",
        "You don't have permission to view center onboarders"
      );
      return;
    }

    new AdminApiModule()
      .getAllCenterOnBoarder()
      .then((res) => {
        let parsedData = [];
        if (res && res.data && res.data.length > 0) {
          parsedData = res.data;
        }
        setCenterOnboardersList(parsedData);
      })
      .catch((err) => {
        console.log(err);
        showDialog(true, "error", "Failed to fetch center onboarders");
      });
  };

  const handleAddCenterOnboarder = () => {
    if (!hasAddPermission()) {
      showDialog(
        true,
        "error",
        "You don't have permission to add center onboarders"
      );
      return;
    }

    navigate("/admin-add-center-onboarder");
  };

  const handleClose = () => {
    setIsCenterOnboarderEdit(false);
  };

  const handleUpdateOnboarder = (id) => {
    if (!hasUpdatePermission()) {
      showDialog(
        true,
        "error",
        "You don't have permission to update center onboarders"
      );
      return;
    }

    navigate("/admin-add-center-onboarder?edit=" + id);
  };

  // Check if user has any access to this section
  if (!hasAnyAccess()) {
    return (
      <div className="access-denied">
        <h2>Access Denied</h2>
        <p>You don't have permission to access this section.</p>
      </div>
    );
  }

  return (
    <div>
      {/* Add Center Onboarder Button - Only show if user has add permission */}
      {hasAddPermission() && (
        <div className="add-service-button">
          <Button
            variant="contained"
            className="text-transform-none"
            onClick={handleAddCenterOnboarder}
          >
            Add a Center Onboarder
          </Button>
        </div>
      )}

      {/* Center Onboarders Table - Only show if user has view permission */}
      {hasViewPermission() && (
        <div>
          <TableContainer component={Paper}>
            <Table sx={{ minWidth: 650 }} aria-label="simple table">
              <TableHead>
                <TableRow>
                  <TableCell>Center Onboarder Id</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Mobile Number</TableCell>
                  <TableCell>Primary Services</TableCell>
                  <TableCell>Allowed States</TableCell>
                  <TableCell>Allowed Cities</TableCell>
                  {hasUpdatePermission() && <TableCell>Action</TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {centerOnboardersList &&
                  centerOnboardersList.length > 0 &&
                  centerOnboardersList.map((row) => (
                    <TableRow
                      key={row._id}
                      sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                    >
                      <TableCell component="th" scope="row">
                        {row.center_onboarder_id || "-"}
                      </TableCell>
                      <TableCell component="th" scope="row">
                        {row.name}
                      </TableCell>
                      <TableCell component="th" scope="row">
                        {row.phone_details.country_code +
                          row.phone_details.mobile_number}
                      </TableCell>
                      <TableCell component="th" scope="row">
                        {row?.services?.primary_services?.service_name}
                      </TableCell>
                      <TableCell component="th" scope="row">
                        {getFormattedList(row?.allowed_states)}
                      </TableCell>
                      <TableCell component="th" scope="row">
                        {getFormattedList(row?.allowed_cities)}
                      </TableCell>
                      {hasUpdatePermission() && (
                        <TableCell component="th" scope="row">
                          <Button
                            variant="outlined"
                            onClick={() => handleUpdateOnboarder(row._id)}
                          >
                            Edit Onboarder
                          </Button>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>

          {centerOnboardersList.length === 0 && (
            <div className="no-data">
              <p>No center onboarders found.</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AdminCenterOnboarders;
